import {
  checkZohoAuth,
  getOrgVariable,
  setUpDataCenter,
  setUpDataCenterUrl,
  getCurrentUser,
  getAllUsers,
} from "../APICalling/zohoAdminApi";
import { getShopifyStores } from "../APICalling/shopifyAdminApi";
import { getCrmFieldsViaShopify } from "../APICalling/shopifyApi";
import { getSettingById } from "../APICalling/settingApi";
import {
  API_KEY_API_NAME,
  ORG_ID_API_NAME,
  ORG_VAR_MAPS_API_NAME,
  SHOPIFY_API_KEY_API_NAME,
  Z_API_KEY_API_NAME,
  ZUID_API_NAME,
  LOG_ERRORS_API_NAME,
  ZOHO_CONN,
  SHOPIFY_SHOP_URL_HEADER_KEY,
  buildShopifyShopUrlHeader,
  SHOPIFY_ORG_CONN,
} from "../config/serviceConstant";

const ZOHO = window.ZOHO;

const authCheckZoho = async ({
  orgId,
  apiKey,
  dataCenterUrl,
  dataCenter,
  shop,
}) => {
  try {
    if (!orgId || !apiKey || !dataCenterUrl) return false;
    const zohoHeaders = {
      orgid: orgId,
      connname: ZOHO_CONN,
      apikey: apiKey,
      datacenterurl: dataCenterUrl,
      datacenter: dataCenter,
      [SHOPIFY_SHOP_URL_HEADER_KEY]: buildShopifyShopUrlHeader(shop),
    };
    return await checkZohoAuth(zohoHeaders);
  } catch (error) {
    return false;
  }
};

// Fast fetch of all Shopify stores/tokens for this org.
// Returns { stores, shop } — shop is the first store domain or "".
// Uses org-level headers (no shop-scoped connname) so the backend returns
// every store registered under this org.
const fetchAllShopifyStores = async ({
  orgId,
  apiKey,
  dataCenterUrl,
  dataCenter,
}) => {
  try {
    if (!orgId || !apiKey) return { stores: [], shop: "" };
    const orgHeaders = {
      orgid: orgId,
      connname: SHOPIFY_ORG_CONN,
      apikey: apiKey,
      datacenterurl: dataCenterUrl,
      datacenter: dataCenter,
    };
    const stores = (await getShopifyStores(orgHeaders)) || [];
    const shop = stores?.[0]?.domain || stores?.[0]?.storeDomain || "";
    return { stores, shop };
  } catch (error) {
    return { stores: [], shop: "" };
  }
};

// Build the base extensionData payload (auth + org vars + stores + entity).
// Shared between initZoho and initZohoCustomAction so per-page initialisers
// only differ in the extras they layer on top.
const buildBaseExtensionData = async (pageData) => {
  const extensionData = {};

  // Fetch org variables
  const orgId = await getOrgVariable(ZOHO, ORG_ID_API_NAME);
  const apiKey = await getOrgVariable(ZOHO, API_KEY_API_NAME);
  const zApiKey = await getOrgVariable(ZOHO, Z_API_KEY_API_NAME);
  const shopifyApiKey = await getOrgVariable(ZOHO, SHOPIFY_API_KEY_API_NAME);
  const orgVarMaps = await getOrgVariable(ZOHO, ORG_VAR_MAPS_API_NAME);
  const zuid = await getOrgVariable(ZOHO, ZUID_API_NAME);
  const logErrors = await getOrgVariable(ZOHO, LOG_ERRORS_API_NAME);
  const dataCenterUrl = await setUpDataCenterUrl(ZOHO);
  const dataCenter = await setUpDataCenter(ZOHO);

  // Fetch user data
  const currentUser = await getCurrentUser(ZOHO);
  const allUsers = await getAllUsers(ZOHO);

  // Parse orgVarMaps if it exists (contains saved auth data)
  let parsedOrgVarMaps = null;
  try {
    if (orgVarMaps) parsedOrgVarMaps = JSON.parse(orgVarMaps);
  } catch (e) {
    parsedOrgVarMaps = null;
  }

  // Fast fetch: pull all registered Shopify stores/tokens up front so
  // shopifyAuth + default shop + stores list are all derived from a
  // single source of truth.
  const { stores: shopifyStores, shop: resolvedShop } =
    await fetchAllShopifyStores({ orgId, apiKey, dataCenterUrl, dataCenter });

  // Prefer backend-known shop; fall back to persisted orgVarMaps
  const shop =
    resolvedShop ||
    parsedOrgVarMaps?.shopifyStoreDomain ||
    parsedOrgVarMaps?.stores?.[0]?.domain ||
    "";

  extensionData.shopifyAuth = shopifyStores.length > 0;
  extensionData.shopifyStores = shopifyStores;

  extensionData.zohoAuth = await authCheckZoho({
    orgId,
    apiKey,
    dataCenterUrl,
    dataCenter,
    shop,
  });

  extensionData.orgId = orgId;
  extensionData.apiKey = apiKey;
  extensionData.dataCenterUrl = dataCenterUrl;
  extensionData.dataCenter = dataCenter;
  extensionData.zApiKey = zApiKey;
  extensionData.shopifyApiKey = shopifyApiKey;
  extensionData.orgVarMaps = parsedOrgVarMaps;
  extensionData.shop = shop;
  extensionData.zuid = zuid;
  extensionData.logErrors = logErrors;
  extensionData.currentUser = currentUser;
  extensionData.allUsers = allUsers;
  extensionData.zohoLoaded = true;

  // PageLoad context (Entity/EntityId for related list widgets).
  //
  // Zoho's PageLoad usually reports the module api_name, but for some
  // standard modules it can report the TAB name instead (e.g. "SalesOrders"
  // for the Sales_Orders module — the tab URL is /tab/SalesOrders). Every
  // consumer keys on the api_name (ENTITY_REGISTRY, ZOHO.CRM.API.getRecord,
  // relatedList settings, field metadata), so normalise here — one place
  // covers RelatedList, Journey and CustomAction alike.
  const ENTITY_ALIASES = {
    SalesOrders: "Sales_Orders",
    PurchaseOrders: "Purchase_Orders",
  };
  const rawEntity = pageData?.Entity || pageData?.module;
  extensionData.entity = (rawEntity && ENTITY_ALIASES[rawEntity]) || rawEntity;
  extensionData.entityId = pageData?.EntityId || null;

  return extensionData;
};

export async function initZoho(callback) {
  try {
    ZOHO.embeddedApp.on("PageLoad", async function (pageData) {
      const extensionData = await buildBaseExtensionData(pageData);
      callback(null, extensionData);
    });
    ZOHO.embeddedApp.init().then(() => {});
  } catch (initZohoError) {
    console.error("initZoho error:", initZohoError);
    callback(initZohoError, null);
  }
}

/**
 * Initialiser for the Custom Action page. Layered on top of the base data:
 *   - savedConfig: the result of ZOHO.CRM.ACTION.getConfig() (or null)
 *   - savedSettingId: the settingId stored against this Custom Action button
 *   - savedSetting: the full setting record fetched via getSettingById, with
 *     `settingSchema` parsed when the backend returns it as a JSON string
 *   - zohoFields: pre-fetched field metadata for the current `entity` so the
 *     wizard's Field Mapping step can render without a second round-trip
 *
 * If a savedSetting is present, the caller can prefill the wizard for editing.
 */
export async function initZohoCustomAction(callback) {
  try {
    ZOHO.embeddedApp.on("PageLoad", async function (pageData) {
      // sample pageData shape:
      // {
      //   module: 'Deals',
      //   configdata: {
      //     settingId: '<uuid>',
      //     orgid: '<org id>',
      //     datacenterurl: 'https://www.zohoapis.com',
      //     datacenter: 'US',
      //     actionType: 'Create Action',
      //     apikey: '<api key>',
      //     connname: 'easyshopifyforzohocrm'
      //   }
      // }
      const extensionData = await buildBaseExtensionData(pageData);

      // 1. Read the saved Custom Action config (if any). Guarded — older SDK
      //    versions may not expose getConfig.
      let savedConfig = null;
      try {
        savedConfig = pageData?.configdata;
      } catch (e) {
        savedConfig = null;
      }
      const savedSettingId = savedConfig?.settingId || "";

      // 2. Fetch the saved setting record so the wizard can prefill.
      let savedSetting = null;
      if (savedSettingId && extensionData.orgId && extensionData.apiKey) {
        try {
          const fetched = await getSettingById(
            {
              orgId: extensionData.orgId,
              apiKey: extensionData.apiKey,
              dataCenterUrl: extensionData.dataCenterUrl,
            },
            savedSettingId
          );
          if (fetched) {
            // Backend may return settingSchema as a JSON string; normalise.
            let schema = fetched.settingSchema || fetched.setting_schema || {};
            if (typeof schema === "string") {
              try {
                schema = JSON.parse(schema);
              } catch {
                schema = {};
              }
            }
            savedSetting = { ...fetched, settingSchema: schema };
          }
        } catch (e) {
          console.error("[initZohoCustomAction] getSettingById failed:", e);
        }
      }

      // 3. Pre-fetch Zoho module fields for the current entity via the
      //    backend's shopify/getcrmfieids proxy. Lets the wizard's Zoho
      //    field pickers render without a direct ZOHO.CRM.META.getFields
      //    round-trip and surfaces additional types (lookup / ownerlookup /
      //    subform) which the SDK call doesn't always include.
      let zohoFields = [];
      if (extensionData.entity) {
        try {
          const crmFieldsHeaders = {
            orgid: extensionData.orgId,
            apikey: extensionData.apiKey,
            datacenterurl: extensionData.dataCenterUrl,
            datacenter: extensionData.dataCenter,
            connname: ZOHO_CONN,
          };
          zohoFields =
            (await getCrmFieldsViaShopify(
              crmFieldsHeaders,
              extensionData.entity
            )) || [];
        } catch (e) {
          console.error(
            "[initZohoCustomAction] getCrmFieldsViaShopify failed:",
            e
          );
        }
      }

      extensionData.savedConfig = savedConfig;
      extensionData.savedSettingId = savedSettingId;
      extensionData.savedSetting = savedSetting;
      extensionData.zohoFields = zohoFields;

      callback(null, extensionData);
    });
    ZOHO.embeddedApp.init().then(() => {});
  } catch (err) {
    console.error("initZohoCustomAction error:", err);
    callback(err, null);
  }
}

//
